import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import Breadcrumbs from "../common/Breadcrumbs";
import { BlogPost, getBlogPosts, postUrl } from "./blogService";
import { useBlogListSeo } from "./useBlogSeo";

const formatDate = (value?: string) =>
  value
    ? new Intl.DateTimeFormat("en", {
        month: "short",
        day: "2-digit",
        year: "numeric",
      }).format(new Date(value))
    : "";

const BlogList = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  useBlogListSeo();

  useEffect(() => {
    getBlogPosts()
      .then(setPosts)
      .finally(() => setLoading(false));
  }, []);

  const filteredPosts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) {
      return posts;
    }

    return posts.filter(post =>
      [post.title, post.excerpt, post.category, ...(post.tags || [])]
        .some(value => String(value || "").toLowerCase().includes(query))
    );
  }, [posts, search]);

  return (
    <div className="main-wrapper">
      <Breadcrumbs title="Blog" subtitle="Blog" />
      <section className="blog-section">
        <div className="container">
          <div className="row justify-content-between align-items-end mb-4">
            <div className="col-lg-7">
              <span className="section-small-title">Alharamain Cab Guides</span>
              <h1 className="display-6 mb-2">Hajj, Umrah and Saudi taxi travel advice</h1>
              <p className="mb-0">
                Practical transport guides for Makkah, Madinah, Jeddah airport transfers, Ziyarat rides, and family bookings.
              </p>
            </div>
            <div className="col-lg-4 mt-3 mt-lg-0">
              <input
                className="form-control"
                value={search}
                onChange={event => setSearch(event.target.value)}
                placeholder="Search blog posts"
                aria-label="Search blog posts"
              />
            </div>
          </div>

          {loading ? (
            <div className="text-center py-5">Loading blog posts...</div>
          ) : (
            <div className="row">
              {filteredPosts.map(post => (
                <div className="col-lg-4 col-md-6 d-flex" key={post.id}>
                  <article className="blog grid-blog w-100">
                    <div className="blog-image">
                      <Link to={postUrl(post)}>
                        <img
                          className="img-fluid"
                          src={post.image || "/assets/img/banner.jpg"}
                          alt={post.imageAlt || post.title}
                          loading="lazy"
                        />
                      </Link>
                    </div>
                    <div className="blog-content">
                      <div className="blog-list-date">
                        <ul className="meta-item-list">
                          <li className="date-icon">
                            <i className="fa-solid fa-calendar-days" /> <span>{formatDate(post.publishedAt)}</span>
                          </li>
                        </ul>
                        {post.category && (
                          <p className="blog-category mb-0">
                            <span>{post.category}</span>
                          </p>
                        )}
                      </div>
                      <h2 className="blog-title h4">
                        <Link to={postUrl(post)}>{post.title}</Link>
                      </h2>
                      <p className="blog-description">{post.excerpt}</p>
                      <Link to={postUrl(post)} className="viewlink btn btn-primary justify-content-center">
                        Read More <i className="feather icon-arrow-right ms-2" />
                      </Link>
                    </div>
                  </article>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default BlogList;
